import { supabase } from '../../config/supabase';
import { Role, RolePermission } from '../../types/cms.types';
import { SYSTEM_MODULES, DEFAULT_ROLES, generatePermissionsFromRole } from '../../config/rolePermissions';

export interface RoleFilter {
    name?: string;
    search?: string;
}

export interface RoleListResponse {
    data: Role[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface CreateRoleRequest {
    name: string;
    description?: string;
    permissions: RolePermission[];
}

export interface UpdateRoleRequest extends CreateRoleRequest {
    id: string;
}

export class RoleService {
    /**
     * Lấy danh sách roles với phân trang và filter
     */
    async getRoles(
        page: number = 1,
        limit: number = 10,
        filters: RoleFilter = {}
    ): Promise<RoleListResponse> {
        try {
            const offset = (page - 1) * limit;
            
            let query = supabase
                .from('roles')
                .select(`
                    *,
                    role_permissions(*)
                `, { count: 'exact' });

            // Apply filters
            if (filters.name) {
                query = query.eq('name', filters.name);
            }

            if (filters.search) {
                query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
            }

            // Add ordering and pagination
            query = query
                .order('created_at', { ascending: false })
                .range(offset, offset + limit - 1);

            const { data, error, count } = await query;

            if (error) {
                throw new Error(`Error fetching roles: ${error.message}`);
            }

            const roles: Role[] = (data || []).map((role: any) => ({
                ...role,
                permissions: role.role_permissions || []
            }));

            const totalPages = Math.ceil((count || 0) / limit);

            return {
                data: roles,
                total: count || 0,
                page,
                limit,
                totalPages
            };
        } catch (error) {
            console.error('Error in getRoles:', error);
            throw error;
        }
    }

    /**
     * Lấy thông tin chi tiết một role
     */
    async getRoleById(id: string): Promise<Role | null> {
        try {
            const { data, error } = await supabase
                .from('roles')
                .select(`
                    *,
                    role_permissions(*)
                `)
                .eq('id', id)
                .single();

            if (error) {
                if (error.code === 'PGRST116') {
                    return null; // Role not found
                }
                throw new Error(`Error fetching role: ${error.message}`);
            }

            return {
                ...data,
                permissions: data.role_permissions || []
            };
        } catch (error) {
            console.error('Error in getRoleById:', error);
            throw error;
        }
    }

    /**
     * Tạo role mới với permissions
     */
    async createRole(request: CreateRoleRequest): Promise<Role> {
        try {
            // Begin transaction
            const { data: roleData, error: roleError } = await supabase
                .from('roles')
                .insert([{
                    name: request.name,
                    description: request.description
                }])
                .select()
                .single();

            if (roleError) {
                throw new Error(`Error creating role: ${roleError.message}`);
            }

            // Insert permissions
            if (request.permissions && request.permissions.length > 0) {
                const permissionsData = request.permissions.map(permission => ({
                    role_id: roleData.id,
                    module_name: permission.module_name,
                    sub_module_name: permission.sub_module_name,
                    view_permission: permission.view_permission,
                    add_permission: permission.add_permission,
                    edit_permission: permission.edit_permission,
                    delete_permission: permission.delete_permission
                }));

                const { error: permissionsError } = await supabase
                    .from('role_permissions')
                    .insert(permissionsData);

                if (permissionsError) {
                    // Rollback role if permissions failed
                    await supabase.from('roles').delete().eq('id', roleData.id);
                    throw new Error(`Error creating role permissions: ${permissionsError.message}`);
                }
            }

            return {
                ...roleData,
                permissions: request.permissions
            };
        } catch (error) {
            console.error('Error in createRole:', error);
            throw error;
        }
    }

    /**
     * Cập nhật role và permissions
     */
    async updateRole(request: UpdateRoleRequest): Promise<Role> {
        try {
            // Update role
            const { data: roleData, error: roleError } = await supabase
                .from('roles')
                .update({
                    name: request.name,
                    description: request.description,
                    updated_at: new Date().toISOString()
                })
                .eq('id', request.id)
                .select()
                .single();

            if (roleError) {
                throw new Error(`Error updating role: ${roleError.message}`);
            }

            // Delete existing permissions
            const { error: deleteError } = await supabase
                .from('role_permissions')
                .delete()
                .eq('role_id', request.id);

            if (deleteError) {
                throw new Error(`Error deleting old permissions: ${deleteError.message}`);
            }

            // Insert new permissions
            if (request.permissions && request.permissions.length > 0) {
                const permissionsData = request.permissions.map(permission => ({
                    role_id: request.id,
                    module_name: permission.module_name,
                    sub_module_name: permission.sub_module_name,
                    view_permission: permission.view_permission,
                    add_permission: permission.add_permission,
                    edit_permission: permission.edit_permission,
                    delete_permission: permission.delete_permission
                }));

                const { error: permissionsError } = await supabase
                    .from('role_permissions')
                    .insert(permissionsData);

                if (permissionsError) {
                    throw new Error(`Error creating new permissions: ${permissionsError.message}`);
                }
            }

            return {
                ...roleData,
                permissions: request.permissions
            };
        } catch (error) {
            console.error('Error in updateRole:', error);
            throw error;
        }
    }

    /**
     * Xóa role
     */
    async deleteRole(id: string): Promise<boolean> {
        try {
            // Kiểm tra xem role có đang được sử dụng không
            const { data: adminRoles, error: checkError } = await supabase
                .from('admin_roles')
                .select('id')
                .eq('role_id', id);

            if (checkError) {
                throw new Error(`Error checking role usage: ${checkError.message}`);
            }

            if (adminRoles && adminRoles.length > 0) {
                throw new Error('Không thể xóa vai trò này vì đã có admin được gán vai trò này');
            }

            // Delete permissions first
            const { error: permissionsError } = await supabase
                .from('role_permissions')
                .delete()
                .eq('role_id', id);

            if (permissionsError) {
                throw new Error(`Error deleting role permissions: ${permissionsError.message}`);
            }

            // Delete role
            const { error } = await supabase
                .from('roles')
                .delete()
                .eq('id', id);

            if (error) {
                throw new Error(`Error deleting role: ${error.message}`);
            }

            return true;
        } catch (error) {
            console.error('Error in deleteRole:', error);
            throw error;
        }
    }

    /**
     * Lấy danh sách modules và permissions có sẵn
     */
    getAvailableModules() {
        return SYSTEM_MODULES;
    }

    /**
     * Tạo role từ template
     */
    async createRoleFromTemplate(templateName: string): Promise<Role> {
        try {
            const template = DEFAULT_ROLES.find(role => role.name === templateName);
            if (!template) {
                throw new Error('Template không tồn tại');
            }

            const permissions = generatePermissionsFromRole(template);
            
            return await this.createRole({
                name: template.name,
                description: template.description,
                permissions: permissions
            });
        } catch (error) {
            console.error('Error in createRoleFromTemplate:', error);
            throw error;
        }
    }

    /**
     * Lấy danh sách templates có sẵn
     */
    getAvailableTemplates() {
        return DEFAULT_ROLES;
    }

    /**
     * Tạo tất cả roles mặc định
     */
    async initializeDefaultRoles(): Promise<void> {
        try {
            for (const template of DEFAULT_ROLES) {
                // Kiểm tra xem role đã tồn tại chưa
                const { data: existingRole } = await supabase
                    .from('roles')
                    .select('id')
                    .eq('name', template.name)
                    .single();

                if (!existingRole) {
                    await this.createRoleFromTemplate(template.name);
                }
            }
        } catch (error) {
            console.error('Error in initializeDefaultRoles:', error);
            throw error;
        }
    }
}
